import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Mail, Edit3, X, Save, RotateCcw, Eye, AlertTriangle, CheckCircle2, Tag,
} from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { useSearchParams } from 'react-router-dom';
import { useFacility } from '../../context/FacilityContext';
import { formatDateTime } from '../../utils/datetime';
import SandboxedHtmlFrame from '../../components/SandboxedHtmlFrame';

/**
 * TemplatesTab — system email templates surface.
 *
 * Fetches GET /api/facilities/{fid}/templates?kind=system. Each row maps a
 * code-defined template_key (services/email_templates.TEMPLATES) to its
 * current state: either the code default OR a facility override row in
 * template_overrides. Operators edit subject + html_body + optional sms_body;
 * Reset deletes the override and falls back to the code default.
 *
 * The rule->template mapping below is derived from the 14 seeded
 * automation_rules and matches services/email_templates.TEMPLATES keys.
 */

// Reverse-lookup: template_key -> the automation rule(s) that fire it.
// Hand-curated from backend/scripts/seed_automation_rules.py to give the
// operator context on what each template does without leaving this view.
const TRIGGER_BY_KEY = {
  account_welcome: 'customer_created',
  rental_confirmed_with_gate_code: 'gate_code_provisioned',
  invoice: 'invoice_created',
  receipt: 'payment_received',
  late_first: 'rent_due_plus_n (+5d)',
  late_second: 'rent_due_plus_n (+10d)',
  lease_send_for_signature: 'rental_created',
  rent_reminder: 'rent_due_minus_n (-3d)',
  pre_lien: 'rent_due_plus_n (+30d)',
  lien_notice: 'rent_due_plus_n (+45d)',
  autopay_failed: 'autopay_failed',
  move_out: 'rental_ended',
  gate_operator_task_pending: 'gate_code_pending',
  // complete_your_rental / pay_to_complete_move_in removed 2026-08-07
  // with the nudge workflow.
  contact_operator_for_code: 'gate_code_failed',
};

const MERGE_TAGS = [
  'tenant_name', 'customer_name', 'first_name', 'customer_email', 'customer_phone',
  'facility_name', 'facility_phone', 'facility_address',
  'unit_number', 'monthly_rate', 'balance', 'due_date', 'start_date', 'end_date',
  'gate_code', 'access_code',
  'pay_url', 'portal_url', 'rent_url', 'signing_url',
  'invoice_number', 'invoice_amount',
];

// ============================================================================
// Editor modal
// ============================================================================
export const TemplateEditorModal = ({ template, facilityId, onClose, onSaved }) => {
  const [form, setForm] = useState({
    subject: template.subject || '',
    html_body: template.html_body || '',
    sms_body: template.sms_body || '',
  });
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);

  const subjectRef = useRef(null);
  const htmlRef = useRef(null);
  const smsRef = useRef(null);
  const focusedRef = useRef('html_body');

  const insertTag = (tag) => {
    const token = `{{${tag}}}`;
    const field = focusedRef.current;
    const ref = field === 'subject' ? subjectRef : field === 'sms_body' ? smsRef : htmlRef;
    const node = ref.current;
    if (!node) {
      setForm((f) => ({ ...f, [field]: (f[field] || '') + token }));
      return;
    }
    const start = node.selectionStart ?? node.value.length;
    const end = node.selectionEnd ?? node.value.length;
    const before = node.value.slice(0, start);
    const after = node.value.slice(end);
    const next = before + token + after;
    setForm((f) => ({ ...f, [field]: next }));
    // Restore caret position after the inserted token.
    requestAnimationFrame(() => {
      node.focus();
      const caret = start + token.length;
      node.setSelectionRange(caret, caret);
    });
  };

  const handleSave = async () => {
    setError(null);
    if (!form.subject.trim() || !form.html_body.trim()) {
      setError('Subject and HTML body are required.');
      return;
    }
    // Save with nothing changed must NOT persist an override: for system
    // templates the editable content is the PREVIEW render (sample data —
    // "Jordan", example.com links), and materializing it verbatim sends
    // those placeholders to real tenants (2026-07-29: 15 tenants got the
    // sample email). No changes -> just close.
    const unchanged =
      form.subject === (template.subject || '') &&
      form.html_body === (template.html_body || '') &&
      (form.sms_body || '') === (template.sms_body || '');
    if (unchanged) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      const res = await apiClient.put(
        `/api/facilities/${facilityId}/templates/${template.template_key}`,
        {
          subject: form.subject,
          html_body: form.html_body,
          sms_body: form.sms_body || null,
        },
      );
      onSaved(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset this template to the code default? Your override will be discarded.')) return;
    setError(null);
    setResetting(true);
    try {
      await apiClient.post(
        `/api/facilities/${facilityId}/templates/${template.template_key}/reset`,
      );
      onSaved({ ...template, has_override: false, is_default: true, _reset: true });
    } catch (e) {
      setError(e.response?.data?.detail || 'Reset failed.');
    } finally {
      setResetting(false);
    }
  };

  const handlePreview = async () => {
    setError(null);
    setPreviewing(true);
    try {
      // Persist the working copy before preview so the server renders what
      // the operator sees. Only do this for system templates that already
      // have edits; otherwise call preview against the saved state.
      if (template.has_override || form.subject !== template.subject || form.html_body !== template.html_body) {
        await apiClient.put(
          `/api/facilities/${facilityId}/templates/${template.template_key}`,
          {
            subject: form.subject,
            html_body: form.html_body,
            sms_body: form.sms_body || null,
          },
        );
      }
      const res = await apiClient.post(
        `/api/facilities/${facilityId}/templates/${template.template_key}/preview`,
      );
      setPreview(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Preview failed.');
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4">
      {/* overscroll-contain: when this panel's own scroll bottoms out (the
          Preview section lives at the bottom), wheel events otherwise CHAIN
          to the page behind the overlay and scroll the Settings chrome out
          of view — so closing the editor landed the operator on a page with
          no header, no tabs, and a full template list, which read as a
          different version of the app. Reported as "templates flip-flopping"
          2026-07-29; the scrollbar position in the report matched the
          chrome's height exactly. */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto overscroll-contain">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-[14px] font-semibold text-slate-900">{template.name || template.template_key}</h3>
            <p className="text-[11px] text-slate-400 font-mono">
              {template.template_key}
              {TRIGGER_BY_KEY[template.template_key] && (
                <> &middot; fires on <span className="text-slate-500">{TRIGGER_BY_KEY[template.template_key]}</span></>
              )}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-700 flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5" /> {error}
            </div>
          )}

          <div>
            <label className="block text-[12px] font-medium text-slate-700 mb-1">Subject</label>
            <input
              ref={subjectRef}
              type="text"
              value={form.subject}
              onFocus={() => { focusedRef.current = 'subject'; }}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] focus:outline-none focus:border-blue-500"
              placeholder="Email subject line"
            />
          </div>

          <div>
            <label className="block text-[12px] font-medium text-slate-700 mb-1">HTML body</label>
            <textarea
              ref={htmlRef}
              value={form.html_body}
              onFocus={() => { focusedRef.current = 'html_body'; }}
              onChange={(e) => setForm({ ...form, html_body: e.target.value })}
              rows={14}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-[12px] font-mono focus:outline-none focus:border-blue-500"
              placeholder="<p>Hi {{first_name}}, ...</p>"
            />
          </div>

          <div>
            <label className="block text-[12px] font-medium text-slate-700 mb-1">SMS body (optional)</label>
            <textarea
              ref={smsRef}
              value={form.sms_body}
              onFocus={() => { focusedRef.current = 'sms_body'; }}
              onChange={(e) => setForm({ ...form, sms_body: e.target.value })}
              rows={3}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-[12px] font-mono focus:outline-none focus:border-blue-500"
              placeholder="Plain-text SMS fallback"
            />
          </div>

          <div>
            <p className="text-[12px] font-medium text-slate-700 mb-1.5 flex items-center gap-1.5">
              <Tag className="h-3 w-3" /> Merge tags
              <span className="text-[10px] text-slate-400 font-normal">click to insert at cursor</span>
            </p>
            <div className="flex flex-wrap gap-1">
              {MERGE_TAGS.map((t) => (
                <button
                  key={t}
                  onClick={() => insertTag(t)}
                  className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-slate-600 font-mono text-[11px] border border-transparent hover:border-blue-200"
                  type="button"
                >
                  {`{{${t}}}`}
                </button>
              ))}
            </div>
          </div>

          {preview && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[12px] font-medium text-slate-700">
                  Preview <span className="text-slate-400">({preview.source})</span>
                </p>
                <button onClick={() => setPreview(null)} className="text-[11px] text-slate-500 hover:text-slate-700">
                  Close preview
                </button>
              </div>
              <p className="text-[11px] text-slate-500 mb-1">
                <span className="font-medium">Subject:</span> {preview.subject}
              </p>
              <SandboxedHtmlFrame
                title="template-preview"
                html={preview.html_body || ''}
                className="w-full h-64 bg-white border border-slate-200 rounded"
              />
              {preview.sms_body && (
                <p className="text-[11px] text-slate-500 mt-2">
                  <span className="font-medium">SMS:</span> {preview.sms_body}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-between gap-2 sticky bottom-0 bg-white">
          <div>
            {template.has_override && (
              <button
                onClick={handleReset}
                disabled={resetting || saving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] text-rose-700 hover:bg-rose-50 rounded-md border border-rose-200"
                type="button"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {resetting ? 'Resetting…' : 'Reset to Default'}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handlePreview}
              disabled={previewing || saving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] text-slate-700 hover:bg-slate-50 rounded-md border border-slate-200"
              type="button"
            >
              <Eye className="h-3.5 w-3.5" /> {previewing ? 'Rendering…' : 'Preview'}
            </button>
            <button
              onClick={onClose}
              disabled={saving}
              className="px-3 py-1.5 text-[13px] text-slate-700 hover:bg-slate-50 rounded-md"
              type="button"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md text-[13px] font-medium"
              type="button"
            >
              <Save className="h-3.5 w-3.5" /> {saving ? 'Saving…' : 'Save Override'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// ============================================================================
// TemplatesTab
// ============================================================================
const TemplatesTab = ({ facilityId }) => {
  const { currentFacility } = useFacility();
  const [searchParams, setSearchParams] = useSearchParams();
  const timeZone = currentFacility?.timezone || undefined;
  const [templates, setTemplates] = useState([]);
  // template_key -> its automation rule (if any). "All Notifications" must
  // say which of these are live: the same registry row is a firing
  // notification when a rule points at it and dormant copy when none does,
  // and the operator asked for a way to move one into Active from here.
  const [rulesByTemplate, setRulesByTemplate] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [info, setInfo] = useState(null);

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(
        `/api/facilities/${facilityId}/templates/`,
        { params: { kind: 'system' } },
      );
      setTemplates(Array.isArray(res.data) ? res.data : []);
      try {
        const rr = await apiClient.get(`/api/facilities/${facilityId}/automation-rules/`);
        const map = {};
        (Array.isArray(rr.data) ? rr.data : []).forEach((r) => {
          if (r.template_key) map[r.template_key] = r;
        });
        setRulesByTemplate(map);
      } catch {
        setRulesByTemplate({});
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load templates.');
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  // Open the template named in the URL.
  //
  // The automation table links here with ?view=templates&template=<key>, but
  // nothing ever read the `template` half — so clicking a template name landed
  // on the full list and looked like it had done nothing. The key is consumed
  // once, then stripped from the URL, so closing the editor does not reopen it
  // and a refresh does not fight the operator.
  const toggleRule = async (rule) => {
    try {
      const res = await apiClient.patch(
        `/api/facilities/${facilityId}/automation-rules/${rule._id || rule.id}`,
        { enabled: !rule.enabled },
      );
      setRulesByTemplate((m) => ({ ...m, [res.data.template_key]: res.data }));
      setInfo(`${res.data.name || res.data.rule_key} ${res.data.enabled ? 'is now active' : 'turned off'}.`);
      setTimeout(() => setInfo(null), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Toggle failed.');
    }
  };

  const requestedKey = searchParams.get('template');
  useEffect(() => {
    if (!requestedKey || !templates.length) return;
    const match = templates.find((t) => t.template_key === requestedKey);
    if (match) setEditing(match);
    const next = new URLSearchParams(searchParams);
    next.delete('template');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedKey, templates]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="text-[13px] text-slate-500">Loading templates…</p>;

  return (
    <div>
      <p className="text-[13px] text-slate-500 mb-4 max-w-3xl">
        These are the code-defined email templates that automation rules fire. Edit a
        template to override the default subject and body for this facility; reset to
        revert to the platform default. Custom one-off templates live on the next tab.
      </p>

      {error && (
        <div className="mb-3 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-700 flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5" /> {error}
        </div>
      )}
      {info && (
        <div className="mb-3 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12px] text-emerald-700 flex items-center gap-2">
          <CheckCircle2 className="h-3.5 w-3.5" /> {info}
        </div>
      )}

      <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left font-medium px-4 py-2.5">Template</th>
                <th className="text-left font-medium px-4 py-2.5">Trigger</th>
                <th className="text-left font-medium px-4 py-2.5 w-40">Active</th>
                <th className="text-left font-medium px-4 py-2.5">Last edited</th>
                <th className="text-right font-medium px-4 py-2.5 w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-slate-400 text-[12px]">
                    No system templates registered.
                  </td>
                </tr>
              )}
              {templates.map((t) => {
                const trigger = TRIGGER_BY_KEY[t.template_key] || '—';
                const customized = !!t.has_override;
                return (
                  <tr key={t.template_key} className="border-t border-slate-100 align-top">
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <Mail className="h-3.5 w-3.5 text-slate-400" />
                        <div>
                          <div className="font-medium text-slate-900">{t.name || t.template_key}</div>
                          <div className="text-[11px] text-slate-400 font-mono">{t.template_key}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[12px] text-slate-700">{trigger}</td>
                    <td className="px-4 py-2.5">
                      {/* The row's LIVE state, and the way to change it —
                          "All Notifications" would otherwise show rows the
                          operator can read but not act on. A toggle here
                          drives the same rule the Active tab's checkbox
                          drives; a template no rule points at cannot be
                          switched on from a list (it has no trigger), so it
                          says so instead of offering a dead control. */}
                      {(() => {
                        const rule = rulesByTemplate[t.template_key];
                        if (!rule) {
                          return (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-50 text-slate-500 ring-1 ring-slate-200"
                                  title="No rule sends this template — it is kept as wording only">
                              Nothing sends this
                            </span>
                          );
                        }
                        // A real switch, not a checkbox. The checkbox version
                        // read as a status badge — the operator asked "where
                        // is the toggle" while looking straight at it. Track
                        // and knob colors are inline so the theme layer
                        // cannot wash them out.
                        return (
                          <button
                            type="button"
                            role="switch"
                            aria-checked={!!rule.enabled}
                            onClick={() => toggleRule(rule)}
                            title={`${rule.enabled ? 'Turn off' : 'Turn on'} — rule ${rule.rule_key}`}
                            className="inline-flex items-center gap-2 group"
                          >
                            <span
                              className="relative inline-block w-9 h-5 rounded-full transition-colors"
                              style={{ backgroundColor: rule.enabled ? '#059669' : '#cbd5e1' }}
                            >
                              <span
                                className="absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all"
                                style={{ left: rule.enabled ? '18px' : '2px' }}
                              />
                            </span>
                            <span className={`text-[11px] font-medium ${rule.enabled ? 'text-emerald-700' : 'text-slate-500'} group-hover:underline`}>
                              {rule.enabled ? 'Active' : 'Off'}
                            </span>
                          </button>
                        );
                      })()}
                      {customized && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 ring-1 ring-amber-200">
                          Customized
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-slate-600">
                      {customized ? (formatDateTime(t.updated_at, timeZone) || '—') : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={() => setEditing(t)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-100 rounded"
                        type="button"
                      >
                        <Edit3 className="h-3 w-3" /> Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <TemplateEditorModal
          template={editing}
          facilityId={facilityId}
          onClose={() => {
            setEditing(null);
            // Came here from Active Notifications via a template-key click?
            // Land back there — being stranded on the All list after closing
            // was reported as the app flip-flopping between two screens.
            if (searchParams.get('from') === 'rules') {
              const next = new URLSearchParams(searchParams);
              next.delete('from');
              next.set('view', 'rules');
              setSearchParams(next, { replace: true });
            }
          }}
          onSaved={(updated) => {
            setEditing(null);
            if (searchParams.get('from') === 'rules') {
              const next = new URLSearchParams(searchParams);
              next.delete('from');
              next.set('view', 'rules');
              setSearchParams(next, { replace: true });
            }
            if (updated && updated._reset) {
              setInfo('Template reset to default.');
            } else {
              setInfo('Template saved.');
            }
            setTimeout(() => setInfo(null), 3000);
            load();
          }}
        />
      )}
    </div>
  );
};

export default TemplatesTab;
